import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { Row, Col, Button, Modal, Collapse, Empty, Spin, message, List, Pagination } from 'antd';
import { historyService } from '../../services/history';
import i18next from '../../i18n';

const { Panel } = Collapse;

const LOGS_PER_PAGE = 25;
const SCROLL_THRESHOLD = 150;

const History = ({ params }) => {
  const [historyModal, setHistoryModal] = useState(false);
  const [pageWindow, setPageWindow] = useState(null);
  const [total, setTotal] = useState(0);
  const [visiblePage, setVisiblePage] = useState(1);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [loadingPreviousPage, setLoadingPreviousPage] = useState(false);

  const containerRef = useRef(null);
  const pageCacheRef = useRef(new Map());
  const fetchingRef = useRef(false);
  const scrollFrameRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const prependedScrollHeightRef = useRef(null);
  const resetScrollRef = useRef(false);

  const logs = [];
  if (pageWindow) {
    for (let page = pageWindow.start; page <= pageWindow.end; page++) {
      logs.push(...(pageCacheRef.current.get(page) || []));
    }
  }

  const lastPage = Math.max(Math.ceil(total / LOGS_PER_PAGE) - 1, 0);

  const fetchLogsPage = (page) =>
    historyService.getLogs({
      data: {
        ...params.data,
        types: params.types,
        page,
        limit: LOGS_PER_PAGE,
      },
    });

  const showLogsError = (error) => {
    message.error({
      content: `Error al cargar el historial de cambios.`,
      style: {
        marginTop: '35vh',
        fontSize: 16,
      },
    });
    console.error(`Error interno: ${error}`);
  };

  const getPage = async (page) => {
    if (pageCacheRef.current.has(page)) {
      return pageCacheRef.current.get(page);
    }

    const response = await fetchLogsPage(page);
    if (!response?.data) {
      throw new Error('Respuesta sin datos del servicio de logs');
    }

    pageCacheRef.current.set(page, response.data);
    const responseTotal = response.pagination?.total;
    if (typeof responseTotal === 'number') {
      setTotal(responseTotal);
    }
    return response.data;
  };

  const openHistoryModal = async () => {
    setHistoryModal(true);
    setLoadingLogs(true);
    setPageWindow(null);
    setTotal(0);
    setVisiblePage(1);
    pageCacheRef.current = new Map();
    lastScrollTopRef.current = 0;

    try {
      await getPage(0);
      setPageWindow({ start: 0, end: 0 });
    } catch (error) {
      showLogsError(error);
    }

    setLoadingLogs(false);
  };

  const loadNextPage = async () => {
    const startingWindow = pageWindow;
    const page = startingWindow.end + 1;
    if (fetchingRef.current || page > lastPage) {
      return;
    }

    fetchingRef.current = true;
    setLoadingNextPage(true);
    try {
      await getPage(page);
      setPageWindow((current) => (current === startingWindow ? { ...current, end: page } : current));
    } catch (error) {
      showLogsError(error);
    }
    fetchingRef.current = false;
    setLoadingNextPage(false);
  };

  const loadPreviousPage = async () => {
    const startingWindow = pageWindow;
    const page = startingWindow.start - 1;
    if (fetchingRef.current || page < 0) {
      return;
    }

    fetchingRef.current = true;
    setLoadingPreviousPage(true);
    try {
      await getPage(page);
      setPageWindow((current) => {
        if (current !== startingWindow) {
          return current;
        }
        prependedScrollHeightRef.current = containerRef.current?.scrollHeight;
        return { ...current, start: page };
      });
    } catch (error) {
      showLogsError(error);
    }
    fetchingRef.current = false;
    setLoadingPreviousPage(false);
  };

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    if (resetScrollRef.current) {
      container.scrollTop = 0;
      lastScrollTopRef.current = 0;
      resetScrollRef.current = false;
    }
    if (prependedScrollHeightRef.current !== null) {
      container.scrollTop += container.scrollHeight - prependedScrollHeightRef.current;
      lastScrollTopRef.current = container.scrollTop;
      prependedScrollHeightRef.current = null;
    }
  }, [pageWindow]);

  const firstVisiblePanelIndex = (container) => {
    const panels = container.querySelectorAll('.ant-collapse-item');
    const containerTop = container.getBoundingClientRect().top;
    for (let index = 0; index < panels.length; index++) {
      if (panels[index].getBoundingClientRect().bottom > containerTop) {
        return index;
      }
    }
    return 0;
  };

  const syncVisiblePage = (container) => {
    const absoluteIndex = pageWindow.start * LOGS_PER_PAGE + firstVisiblePanelIndex(container);
    const pageInView = Math.floor(absoluteIndex / LOGS_PER_PAGE) + 1;
    if (pageInView !== visiblePage) {
      setVisiblePage(pageInView);
    }
  };

  const processScroll = () => {
    const container = containerRef.current;
    if (!container || !pageWindow) {
      return;
    }

    const scrollingDown = container.scrollTop > lastScrollTopRef.current;
    const scrollingUp = container.scrollTop < lastScrollTopRef.current;
    lastScrollTopRef.current = container.scrollTop;

    syncVisiblePage(container);

    const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - SCROLL_THRESHOLD;
    const nearTop = container.scrollTop <= SCROLL_THRESHOLD;
    if (scrollingDown && nearBottom) {
      loadNextPage();
    }
    if (scrollingUp && nearTop) {
      loadPreviousPage();
    }
  };

  const handleScroll = () => {
    if (scrollFrameRef.current) {
      return;
    }
    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      processScroll();
    });
  };

  const handleWheel = (event) => {
    const container = containerRef.current;
    if (container && event.deltaY < 0 && container.scrollTop === 0) {
      loadPreviousPage();
    }
  };

  const scrollToLoadedPage = (page) => {
    const container = containerRef.current;
    const panels = container.querySelectorAll('.ant-collapse-item');
    const target = panels[(page - pageWindow.start) * LOGS_PER_PAGE];
    if (target) {
      container.scrollTop += target.getBoundingClientRect().top - container.getBoundingClientRect().top;
      lastScrollTopRef.current = container.scrollTop;
    }
  };

  const jumpToPage = async (pageNumber) => {
    const page = pageNumber - 1;
    setVisiblePage(pageNumber);

    if (pageWindow && page >= pageWindow.start && page <= pageWindow.end) {
      scrollToLoadedPage(page);
      return;
    }

    const isCached = pageCacheRef.current.has(page);
    if (!isCached) {
      setLoadingLogs(true);
    }

    try {
      await getPage(page);
      resetScrollRef.current = true;
      setPageWindow({ start: page, end: page });
    } catch (error) {
      showLogsError(error);
    }

    if (!isCached) {
      setLoadingLogs(false);
    }
  };

  const logNumber = (index) => total - (pageWindow.start * LOGS_PER_PAGE + index);

  const mapData = (logsData) => {
    if (!logsData || !Object.keys(logsData).length) {
      return null;
    }

    return (
      <Row>
        <Col className="map-data ml-lg-20 ml-xs-5">
          <List
            dataSource={Object.keys(logsData)}
            renderItem={(key) => (
              <Fragment key={`${key}${logsData[key]}`}>
                {typeof logsData[key] === 'object' && !logsData[key].length ? (
                  <List.Item className="ml-15">
                    <span className="bold">{i18next.t(`history.${key}`, { defaultValue: key })}</span>: {mapData(logsData[key])}
                  </List.Item>
                ) : Array.isArray(logsData[key]) && logsData[key].length ? (
                  logsData[key].map((log, index) => {
                    return (
                      <List.Item className="ml-15" key={`${key}${index}`}>
                        <span className="bold">{i18next.t(`history.${key}`, { defaultValue: key })}</span>
                        {mapData(log)}
                      </List.Item>
                    );
                  })
                ) : (
                  <List.Item className="ml-15 pt-5 pb-5">
                    <span className="bold pr-10">{i18next.t(`history.${key}`, { defaultValue: key })}:</span>
                    {i18next.t(`history.${logsData[key]}`, { defaultValue: logsData[key] })}
                  </List.Item>
                )}
              </Fragment>
            )}
          />
        </Col>
      </Row>
    );
  };

  return (
    <Row>
      <Col lg={24} xs={24}>
        <Button className="cmt-button secondary outline" type="primary" onClick={openHistoryModal} size="medium">
          Ver cambios
        </Button>
        <Modal
          className="history-modal"
          responsive
          centered
          open={historyModal}
          onCancel={() => setHistoryModal(false)}
          cancelText="Cerrar"
          footer={false}
        >
          <Row>
            <Col lg={24} xs={24}>
              <h3 className="text-center mb-10">Historial de cambios</h3>
              {loadingLogs ? (
                <Row justify="center">
                  <Spin className="cmt-spin" />
                </Row>
              ) : logs && logs.length > 0 ? (
                <Fragment>
                  {total > LOGS_PER_PAGE && (
                    <Row justify="center" className="mt-15">
                      <Pagination
                        size="small"
                        current={visiblePage}
                        pageSize={LOGS_PER_PAGE}
                        total={total}
                        onChange={jumpToPage}
                        showSizeChanger={false}
                        showTotal={(totalCount) => `${totalCount} cambios`}
                      />
                    </Row>
                  )}
                  <div className="history-logs-wrapper">
                    {loadingPreviousPage && (
                      <Row justify="center" className="history-logs-loader">
                        <Spin className="cmt-spin" />
                      </Row>
                    )}
                    <div ref={containerRef} onScroll={handleScroll} onWheel={handleWheel} className="history-logs-container">
                      <Collapse responsive accordion bordered={false} expandIconPosition="left" className="mt-30">
                        {logs.map((log, index) => {
                          return (
                            <Panel
                              responsive
                              key={pageWindow.start * LOGS_PER_PAGE + index}
                              className="collapse-bar"
                              header={
                                <Row>
                                  <Col lg={1} md={1} sm={1} xs={2}>
                                    <p className="collapse-title-action">{logNumber(index)}</p>
                                  </Col>
                                  <Col lg={7} md={23} sm={23} xs={22}>
                                    <p className="collapse-title-action">{i18next.t(`history.${log.business_context}`, { defaultValue: log.business_context })}</p>
                                  </Col>
                                  <Col lg={8} xs={24}>
                                    <Row>
                                      <span className="collapse-title pr-10">Contacto:</span>
                                      <p className="collapse-title-value mb-0">{log.admin?.email}</p>
                                    </Row>
                                  </Col>
                                  <Col lg={8} xs={24}>
                                    <Row>
                                      <span className="collapse-title pr-10">Fecha y hora:</span>
                                      <p className="collapse-title-value mb-0">{log.date.date}</p>
                                      <p className="collapse-title-value ml-10 mb-0">{log.date.hour} hrs</p>
                                    </Row>
                                  </Col>
                                </Row>
                              }
                            >
                              <Row justify="end" className="panel-data">
                                <Col lg={24} xs={24} className="panel-items">
                                  {mapData(log.data)}
                                </Col>
                                {log.admin && (
                                  <Col lg={23} xs={23} className="panel-data-admin mt-15 p-15">
                                    <Row className="panel-data-admin--row" gutter={10}>
                                      <Col>
                                        <div className="purple-data-bar" />
                                      </Col>
                                      <Col>
                                        <Row className="mt-5 mb-10">
                                          <span className="title-admin">Administrador</span>
                                        </Row>
                                        <Row className="title ml-10">
                                          <span>Nombre:</span>
                                          <p className="title-value ml-10">{log.admin && log.admin.name}</p>
                                        </Row>
                                        <Row className="title ml-10">
                                          <span>Tipo de documento:</span>
                                          <p className="title-value ml-10">{log.admin?.document_type}</p>
                                        </Row>
                                        <Row className="title ml-10">
                                          <span>Número de documento:</span>
                                          <p className="title-value ml-10">{log.admin?.document_number}</p>
                                        </Row>
                                        <Row className="title ml-10">
                                          <span>Email:</span>
                                          <p className="title-value ml-10">{log.admin && log.admin.email}</p>
                                        </Row>
                                      </Col>
                                    </Row>
                                  </Col>
                                )}
                              </Row>
                            </Panel>
                          );
                        })}
                      </Collapse>
                      {loadingNextPage && (
                        <Row justify="center" className="mt-15 mb-15">
                          <Spin className="cmt-spin" />
                        </Row>
                      )}
                    </div>
                  </div>
                </Fragment>
              ) : (
                <Empty description={<span style={{ fontSize: 12 }}>No hay cambios</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Col>
          </Row>
        </Modal>
      </Col>
    </Row>
  );
};

export default History;
